import React, { useState, useCallback, useRef, useEffect, useMemo, memo } from 'react';
import { LogOut, Settings, ChevronUp, Wallet, CreditCard, BadgeCheck } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { logout } from '../../api/auth';
import { fetchUserCredits } from '../../api/user';
import { useAuth } from '../../contexts/AuthContext';
import { useSidebar } from '../../contexts/SidebarContext';
import { useMobile } from '../../contexts/MobileContext';

import styles from './styles/ProfileButton.module.css';

const ProfileButton = () => {
    const [isMenuOpen, setIsMenuOpen] = useState(false);
    const [credits, setCredits] = useState(null);
    const { user, setIsAuthenticated } = useAuth();
    const { closeSidebar } = useSidebar();
    const { isMobile } = useMobile();
    const navigate = useNavigate();
    const menuRef = useRef(null);

    const fetchCredits = useCallback(async () => {
        try {
            const data = await fetchUserCredits();
            setCredits(data);
        } catch (error) {
            console.error('Error fetching credits:', error);
        }
    }, []);

    useEffect(() => {
        if (isMenuOpen) {
            fetchCredits();
        }
    }, [isMenuOpen, fetchCredits]);

    const getInitials = useCallback(() => {
        if (!user) return '';
        const firstName = user.firstName || '';
        const lastName = user.lastName || '';
        return `${firstName.charAt(0)}${lastName.charAt(0)}`.toUpperCase();
    }, [user]);

    const handleClickOutside = useCallback(event => {
        if (menuRef.current && !menuRef.current.contains(event.target)) {
            setIsMenuOpen(false);
        }
    }, []);

    useEffect(() => {
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, [handleClickOutside]);

    const handleLogout = useCallback(async () => {
        try {
            await logout();
            setIsAuthenticated(false);
            navigate('/login');
        } catch (error) {
            console.error('Logout error:', error);
        }
    }, [navigate, setIsAuthenticated]);

    const handleNavigate = useCallback(path => {
        navigate(path);
        if (isMobile) {
            closeSidebar();
        }
        setIsMenuOpen(false);
    }, [navigate, isMobile, closeSidebar]);

    const creditsInfo = useMemo(() => {
        if (!credits) return null;
        if (credits.planName === 'free') {
            return credits.subscriptionBalance.toLocaleString();
        }
        return credits.total.toLocaleString();
    }, [credits]);

    if (!user) {
        return null;
    }

    return (
        <div className={styles.profileContainer} ref={menuRef}>
            <button 
                className={`${styles.profileButton} ${isMenuOpen ? styles.open : ''}`}
                onClick={() => setIsMenuOpen(!isMenuOpen)}
            >
                <div className={styles.avatar}>
                    {user?.avatarUrl ? (
                        <img src={user.avatarUrl} alt="Profile" />
                    ) : (
                        <span>{getInitials()}</span>
                    )}
                </div>
                <div className={styles.userInfo}>
                    <span className={styles.name}>
                        {user?.firstName} {user?.lastName}
                    </span>
                </div>
                <ChevronUp 
                    size={16} 
                    className={`${styles.chevron} ${isMenuOpen ? styles.open : ''}`}
                />
            </button>

            {isMenuOpen && (
                <div className={styles.menu}>
                    {credits && (
                        <div
                            className={styles.credits}
                            onClick={() => handleNavigate('/subscription')}
                        >
                            <Wallet size={16} />
                            <div className={styles.creditsInfo}>
                                <span className={styles.creditsTotal}>
                                    {creditsInfo} credits
                                </span>
                            </div>
                        </div>
                    )}
                    <button onClick={() => handleNavigate('/subscription')}>
                        <CreditCard size={16} />
                        Subscription
                    </button>
                    <button onClick={() => handleNavigate('/settings')}>
                        <Settings size={16} />
                        Settings
                    </button>
                    {user.isAdmin && (
                        <button onClick={() => handleNavigate('/admin/users')}>
                            <BadgeCheck size={16} />
                            Admin
                        </button>
                    )}
                    <button onClick={handleLogout}>
                        <LogOut size={16} />
                        Sign out
                    </button>
                </div>
            )}
        </div>
    );
};

export default memo(ProfileButton);
